import { useState } from "react";
import { Phone, MessageCircle, MapPin, Clock, Mail, Loader2, CheckCircle2 } from "lucide-react";
import SEO from "../components/SEO";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import api from "../lib/api";
import { SITE, callLink, whatsappLink } from "../lib/constants";
import useReveal from "../hooks/useReveal";

export default function Contact() {
  useReveal();
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please enter your name and message");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSuccess(true);
      toast.success("Message received! We'll get back to you soon.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error("Could not submit. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact Lotus Family Dental | Best Dentist in Civil Lines, Prayagraj"
        description="Contact Lotus Family Dental Allahabad. Located at UG4 Ali Tower, MG Marg, Civil Lines, Prayagraj. Call +91 9407018052 or WhatsApp us today."
        keywords="contact dentist allahabad, lotus family dental address"
        canonical="https://lotusfamilydental.me/contact"
      />

      <section className="pt-12 lg:pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold mb-3">
              Get in touch
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-900">
              We are here, <span className="text-teal-700 font-medium">always smiling.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600">
              Walk in, call, or write to us — our front desk is happy to help with appointments, costs and
              treatment guidance.
            </p>
          </div>
        </div>
      </section>

      {/* Map + Info + Form */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8">
            {/* Contact info */}
            <div className="lg:col-span-5 space-y-4">
              <div className="reveal bg-white border border-slate-100 rounded-3xl p-7">
                <div className="w-11 h-11 rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center">
                  <MapPin className="w-5 h-5" />
                </div>
                <h3 className="mt-4 font-heading text-lg font-medium text-slate-900">Visit Us</h3>
                <p className="mt-2 text-slate-600">{SITE.address}</p>
                <a
                  href={SITE.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center text-sm font-medium text-teal-700 hover:underline"
                  data-testid="contact-maps-link"
                >
                  Open in Google Maps →
                </a>
              </div>
              <div className="reveal bg-white border border-slate-100 rounded-3xl p-7">
                <div className="w-11 h-11 rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center">
                  <Phone className="w-5 h-5" />
                </div>
                <h3 className="mt-4 font-heading text-lg font-medium text-slate-900">Call & WhatsApp</h3>
                <a href={callLink} className="mt-2 block text-slate-700 hover:text-teal-700">
                  {SITE.phone}
                </a>
                <div className="mt-3 flex gap-2">
                  <a
                    href={callLink}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-teal-600 text-white text-sm font-medium hover:bg-teal-700"
                    data-testid="contact-call-button"
                  >
                    <Phone className="w-4 h-4" /> Call now
                  </a>
                  <a
                    href={whatsappLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-[#25D366] text-white text-sm font-medium hover:bg-[#1faa55]"
                    data-testid="contact-whatsapp-button"
                  >
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </a>
                </div>
              </div>
              <div className="reveal bg-white border border-slate-100 rounded-3xl p-7">
                <div className="w-11 h-11 rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center">
                  <Clock className="w-5 h-5" />
                </div>
                <h3 className="mt-4 font-heading text-lg font-medium text-slate-900">Working Hours</h3>
                <p className="mt-2 text-slate-600">
                  {SITE.daysOpen}
                  <br />
                  {SITE.timings}
                </p>
              </div>
              <div className="reveal bg-white border border-slate-100 rounded-3xl p-7">
                <div className="w-11 h-11 rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center">
                  <Mail className="w-5 h-5" />
                </div>
                <h3 className="mt-4 font-heading text-lg font-medium text-slate-900">Email</h3>
                <p className="mt-2 text-slate-600 break-all">{SITE.email}</p>
              </div>
            </div>

            {/* Form + Map */}
            <div className="lg:col-span-7 space-y-6">
              <div className="reveal rounded-3xl overflow-hidden border border-slate-100 bg-white">
                <div className="aspect-[16/9] w-full">
                  <iframe
                    title="Lotus Family Dental Allahabad Map"
                    src={SITE.mapsEmbed}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    data-testid="contact-map-iframe"
                  ></iframe>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                className="reveal bg-white border border-slate-100 rounded-3xl p-7 lg:p-9"
                data-testid="contact-form"
              >
                <h2 className="font-heading text-2xl font-medium text-slate-900">Send us a message</h2>
                <p className="mt-1 text-sm text-slate-500">
                  We typically reply within a few hours during clinic hours.
                </p>

                {success && (
                  <div className="mt-5 flex items-start gap-3 bg-teal-50 border border-teal-100 rounded-xl p-4">
                    <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-teal-800">
                      Your message has been received. Our team will get back to you shortly.
                    </div>
                  </div>
                )}

                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-medium text-slate-700">Full Name *</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your name"
                      className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-teal-500"
                      data-testid="contact-name"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-700">Phone</Label>
                    <Input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+91 9..."
                      className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-teal-500"
                      data-testid="contact-phone"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <Label className="text-xs font-medium text-slate-700">Email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@example.com"
                    className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-teal-500"
                    data-testid="contact-email"
                  />
                </div>
                <div className="mt-4">
                  <Label className="text-xs font-medium text-slate-700">Message *</Label>
                  <Textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="How can we help?"
                    rows={4}
                    className="mt-1.5 rounded-xl border-slate-200 focus-visible:ring-teal-500 resize-none"
                    data-testid="contact-message"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    "Send Message"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
